'use client';
import { useAuth } from '@/contexts/AuthContext';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { AlertTriangle, Clock, CheckCircle } from 'lucide-react';
import api from '@/utils/api';

export default function GetVerified() {
	const { user } = useAuth();
	const [timeLeft, setTimeLeft] = useState<{
		days: number;
		hours: number;
		minutes: number;
		seconds: number;
		expired: boolean;
	} | null>(null);

	// fetch status
	const [status, setStatus] = useState<string | null>(null);
	const [rejectionReason, setRejectionReason] = useState<string | null>(null);

	useEffect(() => {
		if (user) {
			console.log("GetVerified: Fetching status for user", user.id);
			api.get('/verifications/status')
				.then((res: { data: { ok: boolean; status: string; verification?: { reason?: string } } }) => {
					console.log("GetVerified: API Response:", res.data);
					if (res.data.ok) {
						setStatus(res.data.status);
						if (res.data.verification?.reason) {
							setRejectionReason(res.data.verification.reason);
						}
					}
				})
				.catch((err: unknown) => {
					console.error("GetVerified: API Error:", err);
				});
		}
	}, [user]);

	// Confetti effect for approved state
	useEffect(() => {
		if ((user?.verified || status === 'approved') && user?.id) {
			const storageKey = `confetti_fired_${user.id}`;
			if (sessionStorage.getItem(storageKey)) return;

			import('canvas-confetti')
				.then((mod) => {
					const fire = mod.default || mod;
					if (typeof fire === 'function') {
						fire({
							particleCount: 100,
							spread: 70,
							origin: { y: 0.6 },
						});
						sessionStorage.setItem(storageKey, 'true');
					} else {
						console.error('Confetti module not a function', mod);
					}
				})
				.catch((err) => console.error('Failed to load confetti', err));
		}
	}, [user?.verified, status, user?.id]);

	// Calculate deadline (7 days from creation)
	useEffect(() => {
		if (!user?.createdAt) return;

		const calculateTimeLeft = () => {
			const createdDate = new Date(
				user.createdAt,
			).getTime();
			const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000;
			const deadline = createdDate + sevenDaysInMs;
			const now = new Date().getTime();
			const difference = deadline - now;

			if (difference <= 0) {
				return {
					days: 0,
					hours: 0,
					minutes: 0,
					seconds: 0,
					expired: true,
				};
			}

			return {
				days: Math.floor(
					difference / (1000 * 60 * 60 * 24),
				),
				hours: Math.floor(
					(difference / (1000 * 60 * 60)) % 24,
				),
				minutes: Math.floor((difference / 1000 / 60) % 60),
				seconds: Math.floor((difference / 1000) % 60),
				expired: false,
			};
		};

		// Initial set
		setTimeLeft(calculateTimeLeft());

		// Update every second
		const timer = setInterval(() => {
			setTimeLeft(calculateTimeLeft());
		}, 1000);

		return () => clearInterval(timer);
	}, [user?.createdAt]);

	// If approved/verified, show green banner (only once)
	const [showSuccessBanner, setShowSuccessBanner] = useState(false);

	useEffect(() => {
		if ((user?.verified || status === 'approved') && user?.id) {
			const startTimeKey = `verified_start_time_${user.id}`;
			const storedStartTime = localStorage.getItem(startTimeKey);

			let startTime: number;
			if (!storedStartTime) {
				startTime = Date.now();
				localStorage.setItem(startTimeKey, startTime.toString());
			} else {
				startTime = parseInt(storedStartTime, 10);
			}

			const twentyFourHours = 24 * 60 * 60 * 1000;
			const isStillValid = (Date.now() - startTime) < twentyFourHours;

			setShowSuccessBanner(isStillValid);
		} else {
			setShowSuccessBanner(false);
		}
	}, [user?.verified, status, user?.id]);

	// Admin check - do not show for admins
	if (user?.role === 'admin') {
		return null;
	}

	if (user?.verified || status === 'approved') {
		if (!showSuccessBanner) return null;
		return (
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
				<section className="rounded-lg shadow-sm p-4 bg-green-50 border border-green-200">
					<div className="flex flex-col md:flex-row justify-between items-center gap-4">
						<div className="flex items-start gap-3">
							<div className="bg-green-100 p-2 rounded-full">
								<CheckCircle className="w-6 h-6 text-green-600" />
							</div>
							<div>
								<h2 className="text-md font-bold text-green-800 mb-1">
									Congratulations, you have successfully been verified
								</h2>
								<p className="text-sm text-green-700 leading-snug max-w-xl">
									Your account is now fully verified. You can now enjoy all the benefits of a verified user on Yunimall.
								</p>
							</div>
						</div>
					</div>
				</section>
			</div>
		);
	}

	// Calculate deadline (7 days from creation)


	if (!user) return null;

	// If pending, show yellow banner
	if (status === 'pending') {
		return (
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
				<section className="rounded-lg shadow-sm p-4 bg-yellow-50 border border-yellow-200">
					<div className="flex flex-col md:flex-row justify-between items-center gap-4">
						<div className="flex items-start gap-3">
							<Clock className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
							<div>
								<h2 className="text-md font-bold text-yellow-800 mb-1">
									Document being reviewed
								</h2>
								<p className="text-sm text-yellow-700 leading-snug max-w-xl">
									Your verification documents have been
									submitted and are currently under review. We
									will notify you once the process is complete.
								</p>
							</div>
						</div>
					</div>
				</section>
			</div>
		);
	}

	// If rejected, show red banner
	if (status === 'rejected') {
		return (
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
				<section className="rounded-lg shadow-sm p-4 bg-red-50 border border-red-200">
					<div className="flex flex-col md:flex-row justify-between items-center gap-4">
						<div className="flex items-start gap-3">
							<AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
							<div>
								<h2 className="text-md font-bold text-red-800 mb-1">
									Verification Rejected
								</h2>
								<p className="text-sm text-red-700 leading-snug max-w-xl">
									Your verification documents were rejected. Please upload valid documents to restore full access.
								</p>
								{rejectionReason && (
									<p className="text-sm font-semibold text-red-800 mt-1">
										Reason: {rejectionReason}
									</p>
								)}
							</div>
						</div>
						<Link
							href="/get-verified"
							className="inline-flex items-center justify-center px-4 py-2 rounded-lg font-bold text-sm shadow-md transition-transform hover:scale-105 active:scale-95 bg-red-600 text-white hover:bg-red-700"
						>
							Update Documents
						</Link>
					</div>
				</section>
			</div>
		);
	}

	// Optional: Hide entirely if expired (or show a 'Disabled' message depending on your logic)
	// For now, I'll show an urgent "Expired" message if time is up.

	return (
		<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
			<section
				className={`rounded-lg shadow-sm p-4 transition-colors ${timeLeft?.expired ? 'bg-red-800' : 'bg-red-600/90'
					}`}
			>
				<div className="flex flex-col md:flex-row justify-between items-center gap-4">
					<div className="flex items-start gap-3">
						<AlertTriangle className="w-6 h-6 text-yellow-300 flex-shrink-0 mt-1" />
						<div>
							<h2 className="text-md font-bold text-white mb-1">
								{timeLeft?.expired
									? 'Verification Period Expired'
									: 'Account Verification Required'}
							</h2>
							<p className="text-sm text-red-100 leading-snug max-w-xl">
								{timeLeft?.expired
									? 'Your verification period has ended. Your account may be disabled. Please upload your matric number and ID immediately.'
									: 'Please upload your matric number and ID to get verified. Unverified accounts will be disabled when the timer runs out.'}
							</p>

							{!timeLeft?.expired && timeLeft && (
								<div className="flex items-center gap-2 mt-2 text-yellow-300 font-mono text-sm font-bold bg-black/20 px-3 py-1 rounded-md w-fit">
									<Clock className="w-4 h-4" />
									<span>
										{timeLeft.days}d : {timeLeft.hours}h :{' '}
										{timeLeft.minutes}m : {timeLeft.seconds}
										s
									</span>
									<span className="text-red-100 font-sans font-normal ml-1">
										remaining
									</span>
								</div>
							)}
						</div>
					</div>

					<div className="flex gap-3 flex-shrink-0">
						<Link
							href="/get-verified"
							className="inline-flex items-center justify-center px-4 py-2 rounded-lg font-bold text-sm shadow-md transition-transform hover:scale-105 active:scale-95"
							style={{
								background: '#FFCF23',
								color: '#051449',
							}}
						>
							Get Verified Now
						</Link>
					</div>
				</div>
			</section>
		</div>
	);
}
